import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import TQConnectServiceRequestAPI from '../../../api/TQConnectServiceRequestAPI';
import {
  ACTIVITY_LOGS_LIMIT,
  SERVICE_REQUEST_RESPONSES_LIMIT,
  SERVICE_REQUESTS_LIMIT,
  TQ_CONNECT_TOP_CONTRIBUTORS_COUNT,
} from '../../../screens/TQConnect/constants';
import { useAppSelector } from '../../../shared/hooks';
import { QUERY_KEYS } from '../../../shared/queryKeys';
import {
  IServiceRequestMessage,
  IServiceRequestMessageRoom,
  IServiceRequestsFilters,
  ITQActivityLog,
  ITQActivityLogsQuery,
  ITQActivityLogsResult,
  ITQServiceRequest,
  ITQServiceRequestDetails,
  ITQServiceRequestResponse,
  ITQServiceRequestResponseDetailsResult,
  ITQServiceRequestResponsesResult,
  ITQServiceRequestsResult,
  ITQServiceRequestStatisticsResult,
  ITQTagsQuery,
  ITQTagsResult,
  ITQTopContributorsQuery,
  ITQTopContributorsResult,
} from '../../../types/tqConnectTypes/service-requests';

/**
 * Custom hook to fetch TQ Connect service requests with infinite scroll
 *
 * @param query - Query parameters for filtering (without page and limit)
 * @param enabled - Whether to enable the query (default: true)
 * @param loadFromCache - Whether to load from cache (default: false)
 * @returns useInfiniteQuery result with service requests data
 */
export const useTQConnectServiceRequests = (
  query: IServiceRequestsFilters,
  enabled: boolean = true,
  loadFromCache: boolean = false,
) => {
  const {
    isLoggedIn,
    hostMetadata: { isCommunityFeatureActive },
  } = useAppSelector((state) => state.app);

  return useInfiniteQuery({
    queryKey: QUERY_KEYS.TQ_CONNECT_SERVICE_REQUESTS(query),
    queryFn: ({ pageParam = 1 }) =>
      TQConnectServiceRequestAPI.getServiceRequests({
        ...query,
        page: pageParam,
        limit: SERVICE_REQUESTS_LIMIT,
      }),
    enabled: isLoggedIn && isCommunityFeatureActive && enabled,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const lastPageResult = lastPage.data.result as ITQServiceRequestsResult;
      const currentItemsCount = allPages.reduce(
        (total, page) =>
          total + (page.data.result as ITQServiceRequestsResult).data.length,
        0,
      );

      // If we have more items to load, return next page number
      if (currentItemsCount < lastPageResult.total) {
        return allPages.length + 1;
      }

      // No more pages to load
      return undefined;
    },
    select: (data) => ({
      data: data.pages.reduce(
        (acc, page) => [...acc, ...page.data.result.data],
        [] as ITQServiceRequest[],
      ),
      total: data.pages[0].data.result.total,
      pageParams: data.pageParams,
    }),
    staleTime: loadFromCache ? Infinity : 0,
  });
};

/**
 * Custom hook to fetch TQ Connect service request statistics
 *
 * @param loadFromCache - Whether to load from cache (default: false)
 * @returns useQuery result with service request statistics data
 */
export const useTQConnectServiceRequestStatistics = (
  loadFromCache: boolean = false,
) => {
  const {
    isLoggedIn,
    hostMetadata: { isCommunityFeatureActive },
  } = useAppSelector((state) => state.app);

  return useQuery({
    queryKey: QUERY_KEYS.TQ_CONNECT_SERVICE_REQUESTS_STATISTICS,
    queryFn: () => TQConnectServiceRequestAPI.getServiceRequestStatistics(),
    enabled: isLoggedIn && isCommunityFeatureActive,
    select: (resp) => resp.data.result as ITQServiceRequestStatisticsResult,
    staleTime: loadFromCache ? Infinity : 0,
  });
};

/**
 * Custom hook to fetch a specific TQ Connect service request by ID
 *
 * @param serviceRequestId - The service request ID to fetch
 * @param loadFromCache - Whether to load from cache (default: false)
 * @returns useQuery result with detailed service request data
 */
export const useTQConnectServiceRequestById = (
  serviceRequestId: string,
  loadFromCache: boolean = false,
) => {
  const {
    isLoggedIn,
    hostMetadata: { isCommunityFeatureActive },
  } = useAppSelector((state) => state.app);

  return useQuery({
    queryKey: QUERY_KEYS.TQ_CONNECT_SERVICE_REQUEST_BY_ID(serviceRequestId),
    queryFn: () =>
      TQConnectServiceRequestAPI.getServiceRequestById(serviceRequestId),
    enabled:
      isLoggedIn && isCommunityFeatureActive && Boolean(serviceRequestId),
    select: (resp) => resp.data.result as ITQServiceRequestDetails,
    staleTime: loadFromCache ? Infinity : 0,
  });
};

/**
 * Custom hook to fetch TQ Connect service request chatroom details
 *
 * @param serviceRequestId - The service request ID to fetch chatroom for
 * @param enabled - Whether to enable the query (default: true)
 * @param loadFromCache - Whether to load from cache (default: false)
 * @returns useQuery result with chatroom details
 */
export const useTQConnectServiceRequestChatRoom = (
  serviceRequestId: string,
  enabled: boolean = true,
  loadFromCache: boolean = false,
) => {
  const {
    isLoggedIn,
    hostMetadata: { isCommunityFeatureActive },
  } = useAppSelector((state) => state.app);

  return useQuery({
    queryKey: QUERY_KEYS.TQ_CONNECT_SERVICE_REQUEST_CHATROOM(serviceRequestId),
    queryFn: () =>
      TQConnectServiceRequestAPI.getServiceRequestChatRoom(serviceRequestId),
    enabled:
      isLoggedIn &&
      isCommunityFeatureActive &&
      Boolean(serviceRequestId) &&
      enabled,
    select: (resp) => resp.data.result as IServiceRequestMessageRoom,
    staleTime: loadFromCache ? Infinity : 0,
  });
};

/**
 * Custom hook to fetch a specific message by ID from service request chatroom
 *
 * @param serviceRequestId - The service request ID
 * @param roomId - The chatroom ID
 * @param messageId - The message ID to fetch
 * @param enabled - Whether to enable the query (default: true)
 * @param loadFromCache - Whether to load from cache (default: false)
 * @returns useQuery result with message details
 */
export const useTQConnectServiceRequestChatRoomMessageById = (
  serviceRequestId: string,
  roomId: string,
  messageId: string,
  enabled: boolean = true,
  loadFromCache: boolean = false,
) => {
  const {
    isLoggedIn,
    hostMetadata: { isCommunityFeatureActive },
  } = useAppSelector((state) => state.app);

  return useQuery({
    queryKey: QUERY_KEYS.TQ_CONNECT_SERVICE_REQUEST_CHATROOM_MESSAGE_BY_ID(
      serviceRequestId,
      roomId,
      messageId,
    ),
    queryFn: () =>
      TQConnectServiceRequestAPI.getServiceRequestChatRoomMessageById(
        serviceRequestId,
        roomId,
        messageId,
      ),
    enabled:
      isLoggedIn &&
      isCommunityFeatureActive &&
      Boolean(serviceRequestId) &&
      Boolean(roomId) &&
      Boolean(messageId) &&
      enabled,
    select: (resp) => resp.data.result as IServiceRequestMessage,
    staleTime: loadFromCache ? Infinity : 0,
  });
};

/**
 * Custom hook to fetch TQ Connect service request responses with infinite scroll
 *
 * @param serviceRequestId - The service request ID to fetch responses for
 * @param enabled - Whether to enable the query (default: true)
 * @param limit - The limit for the number of responses to fetch (default: SERVICE_REQUEST_RESPONSES_LIMIT)
 * @param loadFromCache - Whether to load from cache (default: false)
 * @returns useInfiniteQuery result with service request responses data
 */
export const useTQConnectServiceRequestResponses = (
  serviceRequestId: string,
  enabled: boolean = true,
  limit: number = SERVICE_REQUEST_RESPONSES_LIMIT,
  loadFromCache: boolean = false,
) => {
  const {
    isLoggedIn,
    hostMetadata: { isCommunityFeatureActive },
  } = useAppSelector((state) => state.app);

  return useInfiniteQuery({
    queryKey: QUERY_KEYS.TQ_CONNECT_SERVICE_REQUEST_RESPONSES(
      serviceRequestId,
      limit,
    ),
    queryFn: ({ pageParam = 1 }) =>
      TQConnectServiceRequestAPI.getServiceRequestResponses(serviceRequestId, {
        page: pageParam,
        limit,
      }),
    enabled:
      isLoggedIn &&
      isCommunityFeatureActive &&
      Boolean(serviceRequestId) &&
      enabled,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const lastPageResult = lastPage.data
        .result as ITQServiceRequestResponsesResult;
      const currentItemsCount = allPages.reduce(
        (total, page) =>
          total +
          (page.data.result as ITQServiceRequestResponsesResult).data.length,
        0,
      );

      // If we have more items to load, return next page number
      if (currentItemsCount < lastPageResult.total) {
        return allPages.length + 1;
      }

      // No more pages to load
      return undefined;
    },
    select: (data) => ({
      data: data.pages.reduce(
        (acc, page) => [...acc, ...page.data.result.data],
        [] as ITQServiceRequestResponse[],
      ),
      total: data.pages[0].data.result.total,
      pageParams: data.pageParams,
      myResponse: data.pages[0].data.result?.myResponse,
    }),
    staleTime: loadFromCache ? Infinity : 0,
  });
};

/**
 * Custom hook to fetch a specific TQ Connect service request response by ID
 *
 * @param serviceRequestId - The service request ID
 * @param serviceRequestResponseId - The service request response ID to fetch
 * @param loadFromCache - Whether to load from cache (default: false)
 * @returns useQuery result with detailed service request response data
 */
export const useTQConnectServiceRequestResponseById = (
  serviceRequestId: string,
  serviceRequestResponseId: string,
  loadFromCache: boolean = false,
) => {
  const {
    isLoggedIn,
    hostMetadata: { isCommunityFeatureActive },
  } = useAppSelector((state) => state.app);

  return useQuery({
    queryKey: QUERY_KEYS.TQ_CONNECT_SERVICE_REQUEST_RESPONSE_BY_ID(
      serviceRequestId,
      serviceRequestResponseId,
    ),
    queryFn: () =>
      TQConnectServiceRequestAPI.getServiceRequestResponseById(
        serviceRequestId,
        serviceRequestResponseId,
      ),
    enabled:
      isLoggedIn &&
      isCommunityFeatureActive &&
      Boolean(serviceRequestId) &&
      Boolean(serviceRequestResponseId),
    select: (resp) =>
      resp.data.result as ITQServiceRequestResponseDetailsResult,
    staleTime: loadFromCache ? Infinity : 0,
  });
};

/**
 * Custom hook to fetch TQ Connect tags
 *
 * @param query - Query parameters for filtering and pagination
 * @param loadFromCache - Whether to load from cache (default: true)
 * @returns useQuery result with tags data
 */
export const useTQConnectTags = (
  query: ITQTagsQuery,
  loadFromCache: boolean = true,
) => {
  const {
    isLoggedIn,
    hostMetadata: { isCommunityFeatureActive, creator },
  } = useAppSelector((state) => state.app);

  return useQuery({
    queryKey: QUERY_KEYS.TQ_CONNECT_TAGS({
      ...query,
      creator: creator?._id || undefined,
    }),
    queryFn: () =>
      TQConnectServiceRequestAPI.getTags({
        ...query,
        creator: creator?._id || undefined,
      }),
    enabled: isLoggedIn && isCommunityFeatureActive,
    select: (resp) => resp.data.result as ITQTagsResult,
    staleTime: loadFromCache ? Infinity : 0,
  });
};

/**
 * Custom hook to fetch TQ Connect activity logs with infinite scroll
 *
 * @param query - Query parameters for filtering (without page and limit)
 * @param enabled - Whether to enable the query (default: true)
 * @param loadFromCache - Whether to load from cache (default: false)
 * @returns useInfiniteQuery result with activity logs data
 */
export const useTQConnectActivityLogs = (
  query: Omit<ITQActivityLogsQuery, 'page' | 'limit'> = {},
  enabled: boolean = true,
  loadFromCache: boolean = false,
) => {
  const {
    isLoggedIn,
    hostMetadata: { isCommunityFeatureActive },
  } = useAppSelector((state) => state.app);

  return useInfiniteQuery({
    queryKey: QUERY_KEYS.TQ_CONNECT_ACTIVITY_LOGS(query),
    queryFn: ({ pageParam = 1 }) =>
      TQConnectServiceRequestAPI.getActivityLogs({
        ...query,
        page: pageParam,
        limit: ACTIVITY_LOGS_LIMIT,
      }),
    enabled: isLoggedIn && isCommunityFeatureActive && enabled,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const lastPageResult = lastPage.data.result as ITQActivityLogsResult;
      const currentItemsCount = allPages.reduce(
        (total, page) =>
          total + (page.data.result as ITQActivityLogsResult).data.length,
        0,
      );

      // If we have more items to load, return next page number
      if (currentItemsCount < lastPageResult.total) {
        return allPages.length + 1;
      }

      // No more pages to load
      return undefined;
    },
    select: (data) => ({
      data: data.pages.reduce(
        (acc, page) => [...acc, ...page.data.result.data],
        [] as ITQActivityLog[],
      ),
      total: data.pages[0].data.result.total,
      pageParams: data.pageParams,
    }),
    staleTime: loadFromCache ? Infinity : 0,
  });
};

/**
 * Custom hook to fetch TQ Connect leaderboard (top contributors) - cache is by default true
 *
 * @param query - Query parameters for pagination and cache preferences (preferCachedData is by default true)
 * @param enabled - Whether to enable the query (default: true)
 * @param loadFromCache - Whether to load from cache (default: true)
 * @returns useQuery result with leaderboard data
 */
export const useTQConnectTopContributors = (
  query: ITQTopContributorsQuery = {
    page: 1,
    limit: TQ_CONNECT_TOP_CONTRIBUTORS_COUNT,
    preferCachedData: true,
  },
  enabled: boolean = true,
  loadFromCache: boolean = true,
) => {
  const {
    isLoggedIn,
    hostMetadata: { isCommunityFeatureActive },
  } = useAppSelector((state) => state.app);

  return useQuery({
    queryKey: QUERY_KEYS.TQ_CONNECT_TOP_CONTRIBUTORS(query),
    queryFn: () =>
      TQConnectServiceRequestAPI.getTQConnectTopContributors(query),
    enabled: isLoggedIn && isCommunityFeatureActive && enabled,
    select: (resp) => resp.data.result as ITQTopContributorsResult,
    staleTime: loadFromCache ? Infinity : 0,
  });
};
